import { useContext, useState } from 'react';
import './ConfirmRecord.css';
import './receipt.css';
import { AuthContext } from '../../api/authContext';
import { ApiError, recordSale } from '../../api/client';
import { EventContext } from '../../api/eventContext';
import { syncer } from '../../offline';
import { enqueueSale, makeClientRef } from '../../offline/queue';
import { formatDollars } from '../../shared/functions/formatter';
import { Button, Notice, Select, Stack } from '../../ui';
import { CashierContext } from '../Cashier';

const PAYMENT_LABELS = ['Cash', 'Paynow'] as const;

type ConfirmRecordProps = {
  /** Called once the sale is either recorded or safely queued. */
  onRecorded: () => void;
};

const ConfirmRecord = ({ onRecorded }: ConfirmRecordProps) => {
  const {
    state: { items, bundles, total, savings },
  } = useContext(CashierContext);
  const { logout } = useContext(AuthContext);
  const { events, activeEvent, setActiveEvent } = useContext(EventContext);

  const [payment, setPayment] = useState(0);
  const [recordState, setRecordState] = useState('record');
  const [error, setError] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [queuedNote, setQueuedNote] = useState('');
  /**
   * The order as it was sent, captured when the operator pressed the action.
   *
   * Recording clears the cart (otherwise the persisted order could be sold
   * again), so without this the confirmation would show an empty table and a
   * $0.00 total instead of the receipt for the sale just made.
   */
  const [sent, setSent] = useState<{
    items: SelectedItem[];
    total: number;
    savings: number;
    label: string;
  } | null>(null);

  /**
   * Save the sale on this device instead of failing it.
   *
   * The prices here are the ones the catalogue gave us, so a queued sale is
   * priced at exactly what the customer was charged; the server records that
   * price rather than re-pricing from a catalogue that may have moved.
   *
   * `reuseRef` carries the key from a failed online attempt. Reusing it matters:
   * that attempt may already have been recorded, and the queued entry is then
   * correctly recognised as a duplicate instead of billing a second time.
   */
  /**
   * Build the lines a queued sale must record: just the items, merged so one
   * item never becomes two lines. An applied bundle adds no line of its own —
   * its components are already in the cart — so the saving travels separately
   * as discount_cents.
   */
  const queuedLines = () => {
    const merged = new Map<number, { item_id: number; title: string; price_cents: number; quantity: number }>();
    for (const item of items) {
      const existing = merged.get(item.id);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        merged.set(item.id, {
          item_id: item.id,
          title: item.title,
          price_cents: Math.round(item.price * 100),
          quantity: item.quantity,
        });
      }
    }
    return [...merged.values()];
  };

  /** A human snapshot of what was discounted, capped to the column size. */
  const bundleLabel = () =>
    bundles
      .map((bundle) => (bundle.quantity > 1 ? `${bundle.title} x${bundle.quantity}` : bundle.title))
      .join(', ')
      .slice(0, 160);

  // What the receipt shows: the sent snapshot once the order has gone, the live
  // cart before that (the two are identical until recording clears the cart).
  const receipt = sent ?? { items, total, savings, label: bundleLabel() };

  const queueLocally = (reason: string, reuseRef?: string) => {
    const queued = enqueueSale({
      client_ref: reuseRef ?? makeClientRef(),
      payment: payment === 0 ? 'cash' : 'paynow',
      items: queuedLines(),
      total_cents: Math.round(total * 100),
      discount_cents: Math.round(savings * 100),
      discount_label: bundleLabel(),
      sold_at: new Date().toISOString(),
      catalog_revision: null,
      // Recorded now, not resolved at sync time: the booth the sale happened at
      // is not knowable later, and the event may have been archived by then.
      event_id: activeEvent?.id ?? null,
    });

    if (!queued.ok) {
      setRecordState('record');
      setError(queued.reason ?? 'Could not save this sale on the device.');
      return;
    }

    setError('');
    setQueuedNote(reason);
    setRecordState('queued');
    // The order is finished from the till's point of view either way.
    onRecorded();
    // Try straight away; if we are actually back online this syncs immediately.
    syncer.poke();
  };

  /**
   * One key per order, reused by every attempt.
   *
   * `useState`'s initialiser runs once, so this stays stable across retries and
   * re-renders. That stability is the whole point: an attempt carrying the same
   * key is recognised by the server as the same sale, so a lost response cannot
   * turn into a second charge.
   */
  const [clientRef] = useState(() => makeClientRef());

  const recordPayment = () => {
    // An event is required. This is a safety net behind the disabled button: a
    // sale already rung up must be filed somewhere, never silently dropped.
    if (!activeEvent) {
      setError('Choose the booth event this sale belongs to first.');
      return;
    }

    const lineItems = items.map((item) => ({ item_id: item.id, quantity: item.quantity }));
    const bundleLines = bundles.map((bundle) => ({ bundle_id: bundle.bundleId, quantity: bundle.quantity }));
    const paymentKind = payment === 0 ? 'cash' : 'paynow';

    recordSale({
      payment: paymentKind,
      items: lineItems,
      bundles: bundleLines,
      clientRef,
      eventId: activeEvent.id,
    })
      .then((receipt) => {
        setError('');
        setOrderRef(receipt.order_ref);
        setRecordState('success');
        onRecorded();
        syncer.poke();
      })
      .catch((err: unknown) => {
        setOrderRef('');
        if (err instanceof ApiError && err.status === 401) {
          logout();
          return;
        }

        if (err instanceof ApiError && err.status === 0) {
          // A transport failure. The request may or may not have been recorded —
          // the *response* is what went missing. Queueing is safe either way
          // because the entry carries the same `clientRef`: if the sale did land,
          // the sync is recognised as a duplicate; if it did not, the sync
          // records it. Exactly one sale results, and the syncer does the
          // retrying rather than a tight in-place loop.
          queueLocally('No connection to the server. This sale is saved on the device and will sync automatically.', clientRef);
          return;
        }

        setRecordState('record');
        setError(err instanceof Error ? err.message : 'Please try again!');
      });
  };

  return (
    <div className="confirmRecordContainer">
      <div className="denseTable">
        <table>
          <thead>
            <tr>
              <th>S/N</th>
              <th align="left">Name</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {receipt.items.map(({ title, quantity, price, subtotal }, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td align="left">{title}</td>
                <td align="right" className="money">
                  <div>{formatDollars(price)}</div>
                </td>
                <td>{quantity}</td>
                <td align="right" className="money">
                  <div>{formatDollars(subtotal)}</div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Savings above the total, and both rows built the same way, so the
                labels line up with each other and the amounts line up with the
                item subtotals. */}
            {receipt.savings > 0 && (
              <tr className="confirmSavingsRow">
                {/* Empty first cell mirrors the total row: the narrow-screen
                    CSS hides tfoot column 1, and without it the label would
                    vanish and leave the amount orphaned. */}
                <td />
                {/* The bundle names live in the tooltip: the receipt is narrow,
                    and the cart already spells them out, so a long name here
                    would wrap and push the amount out of line. */}
                <td colSpan={2} align="right" title={receipt.label}>
                  bundle savings:
                </td>
                <td colSpan={2} align="right" className="money">
                  <div>{formatDollars(receipt.savings)}</div>
                </td>
              </tr>
            )}
            <tr className="confirmTotalRow">
              <td />
              <td colSpan={2} align="right">
                Total:
              </td>
              {/* The amount keeps two columns of room so the larger total stays
                  inside the table and still ends on the subtotal edge. */}
              <td colSpan={2} align="right" className="money">
                <div>{formatDollars(receipt.total)}</div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="confirmRecordPay">
        <span>Select Payment Type:&nbsp;{PAYMENT_LABELS[payment]}</span>
        <Stack direction="row" gap={4}>
          <Button size="sm" variant={payment === 0 ? 'primary' : 'secondary'} onClick={() => setPayment(0)}>
            Cash
          </Button>
          <Button size="sm" variant={payment === 1 ? 'primary' : 'secondary'} onClick={() => setPayment(1)}>
            Paynow
          </Button>
        </Stack>
      </div>
      <div className="confirmRecordEvent">
        <span className="confirmRecordEventLabel">Booth event</span>
        {activeEvent ? (
          <strong>{activeEvent.name}</strong>
        ) : events.length === 0 ? (
          <span className="confirmRecordEventEmpty">No events yet — create one on the events page before recording a sale.</span>
        ) : (
          /* Fallback for an unset current event: pick it here rather than sending
             the customer away, and remember the choice for the rest of the booth. */
          <Select
            value=""
            onChange={(changed) => {
              const chosen = events.find((entry) => entry.id === Number(changed.target.value));
              if (chosen) setActiveEvent(chosen);
            }}
          >
            <option value="" disabled>
              choose an event…
            </option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </Select>
        )}
      </div>
      <div className="confirmRecordActions">
        {error && <Notice variant="error">{error}</Notice>}
        {recordState === 'success' && orderRef && (
          <Notice variant="success" className="confirmRecordSuccess">
            recorded as {orderRef}
          </Notice>
        )}
        {recordState === 'queued' && (
          <Notice variant="warning" className="confirmRecordQueued">
            <strong>saved offline</strong>
            <span>{queuedNote}</span>
          </Notice>
        )}
        {/* Once the sale is recorded the action is spent: the cart has already
            been cleared, so a second press would post an empty line list, which
            the server refuses - replacing the receipt with an error at the
            moment the operator most needs the confirmation. */}
        <Button
          variant="primary"
          onClick={() => {
            setSent({ items, total, savings, label: bundleLabel() });
            setRecordState('recording');
            recordPayment();
          }}
          disabled={recordState === 'recording' || recordState === 'queued' || recordState === 'success' || !activeEvent}
          title={activeEvent ? undefined : 'Choose a booth event first.'}
        >
          {recordState}!
        </Button>
      </div>
    </div>
  );
};

export default ConfirmRecord;
